import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { listCategories, type CategoryInfo } from '../../api/discover';
import { setTopics, type Post } from '../../api/posts';
import { Alert, Button, Card, Input } from '../ui';
import './StoryTopics.css';

export interface StoryTopicsProps {
  post: Post;
  onChanged: (post: Post) => void;
}

/**
 * What a story is about.
 *
 * Two different things that look alike, so they are shown as two: a **category**
 * is chosen from a short list someone curates — it is how a reader who does not
 * know what they want narrows the field — and **tags** are the author's own
 * words, worth exactly as much as the number of stories that share them.
 *
 * Neither is content, so changing them does not make a new version: what a
 * reader read does not change because the author re-shelved it.
 */
export function StoryTopics({ post, onChanged }: StoryTopicsProps) {
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [category, setCategory] = useState(post.category?.slug ?? '');
  const [tags, setTags] = useState(post.tags.map((tag) => tag.name).join(', '));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    void listCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await setTopics(post.id, {
        category,
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      });
      onChanged(updated);
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="What this is about"
      description="How someone who is not looking for you might find it. Not part of the story: changing this makes no new version."
    >
      {error && (
        <Alert tone="warning" title="That could not be saved">
          {error.message}
        </Alert>
      )}

      <div className="sc-topics">
        <label className="sc-topics__field">
          <span>Category</span>
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">No category</option>
            {categories.map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>

        <Input
          label="Tags"
          value={tags}
          placeholder="coffee, retail, monterrey"
          onChange={(event) => setTags(event.target.value)}
        />

        {/* Not just "Save": the page already has one, and the two save
            different things — the words, and where the words are shelved. */}
        <Button size="sm" isLoading={saving} onClick={() => void save()}>
          Save topics
        </Button>
      </div>

      {saved && !error && <p className="sc-topics__saved">Saved.</p>}
    </Card>
  );
}
